// TEMPORARY: Cara Standard plan sunset/migration flow. Remove all renewal-migration
// code once every agency has migrated off the Standard plan.
import { SubscriptionPeriod, SubscriptionPlan, SubscriptionState } from '~/api/graphql';
import {
  getRenewalPromptStartDate,
  parseSubscriptionDate,
  SubscriptionRenewalDates
} from './renewal-billing-date';
import { RENEWAL_PLAN_OPTIONS } from './annual-renewal';

export const getRenewalDiscountPercent = (period: SubscriptionPeriod) =>
  period === SubscriptionPeriod.Annual ? 20 : 10;

export const getRenewalPlanOption = (plan: SubscriptionPlan) =>
  RENEWAL_PLAN_OPTIONS.find((option) => option.id === plan) ?? RENEWAL_PLAN_OPTIONS[0];

export const SUPPORT_EMAIL = 'support@getcara.ai';

/** Days before renewal when the migration banner becomes visible. */
export const RENEWAL_MIGRATION_WINDOW_DAYS = 30;

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

type SubscriptionLike = SubscriptionRenewalDates & {
  state?: SubscriptionState | null;
  plan?: SubscriptionPlan | null;
  standardSunsetRenewalConfirmedAt?: string | Date | null;
  standardSunsetRenewalBannerDismissedAt?: string | Date | null;
};

/**
 * True once an agency is within RENEWAL_MIGRATION_WINDOW_DAYS of the end of its
 * Standard term.
 */
export const isWithinRenewalMigrationPromptWindow = (
  subscription?: SubscriptionRenewalDates | null,
  now: Date = new Date()
): boolean =>
  startOfLocalDay(now) >=
  startOfLocalDay(getRenewalPromptStartDate(subscription, RENEWAL_MIGRATION_WINDOW_DAYS));

export const formatRenewalPlanLabel = (plan?: SubscriptionPlan | null): string =>
  getRenewalPlanOption(plan ?? SubscriptionPlan.Pro).name;

export const formatRenewalPeriodLabel = (period?: SubscriptionPeriod | null): string =>
  period === SubscriptionPeriod.Annual ? 'Annual' : 'Monthly';

export const isLegacyStandardActiveSubscription = (
  subscription?: SubscriptionLike | null
): boolean =>
  subscription?.state === SubscriptionState.Active &&
  subscription?.plan === SubscriptionPlan.Standard;

/** Legacy Standard on Active or Trial (first-year trial agencies still need sunset migration). */
export const isLegacyStandardSubscription = (subscription?: SubscriptionLike | null): boolean =>
  (subscription?.state === SubscriptionState.Active ||
    subscription?.state === SubscriptionState.Trial) &&
  subscription?.plan === SubscriptionPlan.Standard;

export const shouldShowRenewalMigrationBanner = (
  subscription?: SubscriptionLike | null,
  now: Date = new Date()
): 'migration' | 'confirmed' | false => {
  if (subscription?.standardSunsetRenewalBannerDismissedAt) {
    return false;
  }
  if (subscription?.standardSunsetRenewalConfirmedAt) {
    return 'confirmed';
  }
  if (
    isLegacyStandardSubscription(subscription) &&
    isWithinRenewalMigrationPromptWindow(subscription, now)
  ) {
    return 'migration';
  }
  return false;
};

/** Billing start for sunset renewals: trial end when present, else renewal anniversary. */
export const getRenewalBillingStartDate = (trialEndDate?: string | Date | null): Date | null => {
  if (!trialEndDate) {
    return null;
  }
  return parseSubscriptionDate(String(trialEndDate));
};

export const formatRenewalShortDate = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
